import Vue from 'vue';
import Component from 'vue-class-component';
import {DownloadHelper} from '../../../../helpers/download';
import {Watch} from 'vue-property-decorator';
import * as screenfull from 'screenfull';
import {DataManager, Visualisation, Toolbar, Chart, FilterBar, CustomDynamicView} from 'wc-visualisation';
import {downloadCsv, arrayToCsv, Table, Icon, Tooltip, pivotData, formatDate, formatNumber, pivotDataWithAggregation} from 'wc-lib';
import {format} from 'date-fns';
import { url } from 'inspector';
import {NemRegions, FuelTypeMap} from '../../config';
import {RenewableFuelMix} from './fuel-mix';


require('wc-lib/icons/downloadcircle.svg');
require('wc-lib/icons/fullscreen.svg');
require('../../../../icons/aim.svg');

@Component({
  template: require('./renewable-penetration.html'),
  name: 'renewable-penetration',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'vis-table': Table,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-tooltip': Tooltip,
    'fuel-mix': RenewableFuelMix
  }
})
export class NemRenewablePenetration extends Vue {
  isFullscreen = false;
  data: Array<any> = [];
  apiData: Array<any> = [];
  dynamicView: CustomDynamicView<any>;
  regions = [];
  selectedRegion: string = 'ALL';
  selectedInterval: string = 'MONTHRENEWABLEPENETRATION'; 
  currentInterval: any = null;
  id: any = {};
  currentFuelTypes = [];
  totalFuelTypes = [];
  chartConfig: any  = null;
  fuelTypes = [];
  fuelTypeMap = FuelTypeMap;
  showCursor = false;
  chartObject = null;
  chartCursorCallback = null;
  renewableFuels = ['Solar', 'Utility-scale Solar', 'Distributed PV', 'Wind', 'Hydro', 'Biomass', 'Battery'];
  monthlyRenewableMaxMin = [];
  allTimeRenewableMax = {};
  allTimeRenewableMin = {};
  monthRecord = {};
  historicMonthRecord = {};
  renewableColour = '#A1D978';
  nonRenewableColour = '#FE5F55';
  maxFuelMixVisible = false;

  formatNumber = formatNumber;

  getFuelTypeValue(value: number) {
    if (this.selectedInterval === 'MONTHRENEWABLEPENETRATION') {
      return value < 10000 ? 'MW' : 'GW';
    } else {
      return value < 10000 ? 'MWh' : 'GWh';
    }
  }

  get currentIntervalDateTime() {
    if (this.selectedInterval == 'CURRENT' && this.data.length > 0) 
      return '(' + formatDate(this.data[0].DATETIME, 'd MMMM yyyy - HH:mm') + ')';

    return '';
  }

  created() {
    this.regions.push({
      value: 'ALL',
      label: 'NEM'
    });
    if (screenfull.isEnabled) {
      (screenfull as any).onchange(() => {
        this.isFullscreen = (screenfull as any).isFullscreen;
      });
    }
    this.fetchData();
  }
  
  async fetchData() {
    let result = await DataManager.addEndPoint('../api/report/FUEL', {"type":[this.selectedInterval]}, undefined, `FUEL_RENEWABLE_PENETRATION_MONTH`, undefined, 'yyyy-MM-ddTHH:mm:ss', ['DATETIME']);
    this.id[this.selectedInterval] = result;
    this.dynamicView = DataManager.getDynamicView(this.id[this.selectedInterval], `${this.selectedInterval}-fuel-mix`);
    this.applyFilter();
  }

  @Watch('selectedInterval')
  @Watch('selectedRegion')
  filterChange() {
    this.currentInterval = null;
    this.fetchData();
  } 

  selectRegion(region: string) {
    this.selectedRegion = region;
  }

  applyFilter() {
    let fuelTypes = [];
    this.dynamicView.removeFilters();
    if (this.selectedRegion !== 'ALL')
      this.dynamicView.applyFind({ 'STATE': this.selectedRegion });
    let data = this.dynamicView.data();
    this.apiData = data;
    for (const row of data) {
      if (fuelTypes.indexOf(row.FUEL_TYPE) === -1) {
        fuelTypes.push(row.FUEL_TYPE);
      }
    }

    data = pivotDataWithAggregation(data, ['DATETIME', 'TYPE'], 'FUEL_TYPE', 'SUPPLY');
    if (data.length > 0 && !this.currentInterval)
      this.currentInterval = data[data.length - 1].DATETIME;

    this.fuelTypes = fuelTypes.sort();
    this.data = data;
    

    if (this.selectedInterval === 'CURRENT' && data.length > 0) {
      this.setCurrentFuelTypes(data[0]);
    }

    this.totalFuelTypes = this.getTotalFuelTypes(data)?.totalFuelTypes;
    this.monthlyRenewableMaxMin = this.getMonthlyRenewableMaxMin(data);
    const allTimeRecords = this.getAllTimeRecords(this.monthlyRenewableMaxMin);
    this.allTimeRenewableMin = allTimeRecords.min;
    this.allTimeRenewableMax = allTimeRecords.max;

    if (this.monthlyRenewableMaxMin && this.monthlyRenewableMaxMin.length > 0) {
      this.setCurrentFuelTypes(this.monthlyRenewableMaxMin[this.monthlyRenewableMaxMin.length - 1]);
    }

    this.chartConfig = this.getChartConfig();
  }

  toggleFullscreen() {
    (screenfull as any).toggle();
  }

  download() {
    const columns = [
      {      
        title: 'DateTime',
        field: 'DATETIME',
        formatter: (row) => {
          return formatDate(row.DATETIME, 'dd/MM/yyyy HH:mm');
        }
      },
      {      
        title: 'Max/Min',
        field: 'TYPE'
      },
      {      
        title: 'State',
        field: 'STATE',
        formatter: (row) => {
          return row.STATE.replace('1', '');
        }
      },
      {      
        title: 'Fuel Type',
        field: 'FUEL_TYPE'
      },
      {      
        title: 'Supply',
        field: 'SUPPLY'
      }
    ];
    let csv = arrayToCsv(this.apiData, columns);
    downloadCsv(`NEM_RENEWABLE_PENETRATION_${this.selectedRegion}_${formatDate(new Date(), 'yyyyMMddHHmm')}.csv`, csv);
  }

  getAllTimeRecords(data: any[]) {
    let currentMax = { maxPercentage: null };
    let currentMin = { minPercentage: 1 };

    for (const row of data) {
      if (row.maxPercentage > currentMax.maxPercentage) {
        currentMax = row;
      }

      if (row.minPercentage < currentMin.minPercentage) {
        currentMin = row;
      }
    }

    return { max: currentMax, min: currentMin };
  }

  getMonthlyRenewableMaxMin(data : any[]) {
    const renewableMaxMinByMonth = [];
    for (const row of data) {
      const intervalFuelTypes = this.getTotalFuelTypes([row]);
      const rowMonth =  new Date(row.DATETIME.getFullYear(), row.DATETIME.getMonth(), 1);

      let renewableMonth = renewableMaxMinByMonth.find(x => x.month.getFullYear() === rowMonth.getFullYear() && x.month.getMonth() === rowMonth.getMonth());
      if (!renewableMonth) {
        renewableMonth = {
          month: rowMonth
        };

        renewableMaxMinByMonth.push(renewableMonth);
      }
        if (row.TYPE === 'Max') {
          renewableMonth.maxPercentage = intervalFuelTypes.percentageRenewables;
          renewableMonth.maxTime =  row.DATETIME;
          renewableMonth.maxTotal =  intervalFuelTypes.totalRenewables;
          renewableMonth.fuelMix = intervalFuelTypes.totalFuelTypes;
          renewableMonth.nonRenewabletotal = intervalFuelTypes.total - intervalFuelTypes.totalRenewables;
        } else {
          renewableMonth.minPercentage = intervalFuelTypes.percentageRenewables;
          renewableMonth.minTime = row.DATETIME;
          renewableMonth.minTotal =  intervalFuelTypes.totalRenewables;
        }

      renewableMonth.maxNonRenewablePercentage = 1 - renewableMonth.maxPercentage;
    }

    return renewableMaxMinByMonth;
  }

  getTotalFuelTypes(data : any[]) {
    const totalFuelTypes = {};
    const totalFuelTypesArray = [];
    let total = 0;
    let totalRenewables = 0;

    for (const row of data) {
      for (const property in row) {
        if (property === 'DATETIME' || property === 'TYPE') continue;

        const value = row[property];
        const title =  property.replace(' SUPPLY', '');

        let fuelTypeMap = this.fuelTypeMap[title];
        if (!fuelTypeMap) {
          fuelTypeMap = this.fuelTypeMap.default;
        }

        if (totalFuelTypes[title]) {
          totalFuelTypes[title].value += value;
        } else {
          totalFuelTypes[title] = {
            title: title,
            value: value,
            icon: fuelTypeMap.icon,
            colour: fuelTypeMap.colour,
            border: fuelTypeMap.border,
            renewable: fuelTypeMap.renewable
          };
        }
        total += value;

        if (this.renewableFuels.includes(title)) {
          totalRenewables += value;
        }
      }
    }

    for (const fuelType in totalFuelTypes) {
      const totalFuelType = totalFuelTypes[fuelType];
      totalFuelType.percentage = totalFuelType.value / total;
      totalFuelTypesArray.push(totalFuelType);
    }


    totalFuelTypesArray.sort((a,b) => {
      if (a.title < b.title) {
        return -1;
      }
      if (a.title > b.title) {
        return 1;
      }
      return 0;
    });
    return { totalFuelTypes: totalFuelTypesArray, total: total, totalRenewables: totalRenewables, percentageRenewables: totalRenewables / total  };
  }

  setCurrentFuelTypes(row: any) {
    const historicMonth =  new Date(row.month.getFullYear()-1, row.month.getMonth(), 1);
    this.monthRecord = row;
    this.historicMonthRecord = this.monthlyRenewableMaxMin.find(x => x.month.getFullYear() === historicMonth.getFullYear() && x.month.getMonth() === historicMonth.getMonth());
  }

  chartRendered(chart: any) {
    this.chartObject = chart;
    this.chartCursorCallback = this.setChartCursor.bind(this);
    this.setChartCursor({chart: chart});
    chart.addListener('drawn', this.chartCursorCallback);
  }

  setChartCursor(config: any) {
    if (this.$refs.mixByTime && (this.$refs.mixByTime as HTMLElement).offsetHeight > 0) {
      if (config.chart.chartCursor.showCursorAt)
        config.chart.chartCursor.showCursorAt(this.currentInterval);
    }
  }

  getChartConfig() {
    if ((this.chartObject) && (this.chartObject.listenersAdded))
      this.chartObject.removeListener(this.chartObject, 'drawn', this.chartCursorCallback);

    this.chartObject = null;
    const graphs = [];
    const guides = [];

    graphs.push({
      valueAxis: "v1", 
      valueField: 'maxPercentage', 
      lineColor: this.renewableColour, 
      lineThickness: 1, 
      fillAlphas: 1,
      fillColors: [this.renewableColour],
      showBalloon: true,
      balloonFunction: (value) => { return `${formatNumber(value.dataContext.maxPercentage * 100, "0,00.0")}% - ${formatNumber(value.dataContext.maxTotal, "0,00")} MW`},
      type: "column"
    });

    graphs.push({
      valueAxis: "v1", 
      valueField: 'maxNonRenewablePercentage', 
      lineColor: this.nonRenewableColour, 
      lineThickness: 1, 
      fillAlphas: 1,
      fillColors: [this.nonRenewableColour],
      showBalloon: true,
      balloonFunction: (value) => { return `${formatNumber(value.dataContext.maxNonRenewablePercentage * 100, "0,00.0")}% - ${formatNumber(value.dataContext.nonRenewabletotal, "0,00")} MW`},
      type: "column"
    });

    for (const row of this.monthlyRenewableMaxMin) {
      if (row.month.getMonth() === 0) {
        guides.push({
          date: row.month,
          lineColor: "#ffffff",
          lineAlpha: 1,
          dashLength: 0,
          inside: true,
          above: true
        });
      }
    }
    return {
      type: 'serial',
      columnWidth: 1,
      marginRight: 0,
      marginLeft: 0,
      marginTop: 1,
      marginBottom: 0,
      addClassNames: true,
      panEventsEnabled: false,
      numberFormatter: {
        precision: 2, 
        decimalSeparator: '.', 
        thousandsSeparator: ','
      },
      valueAxes: [{
        id: "v1",
        position: "left",
        labelsEnabled: false,
        gridAlpha: 0,
        axisAlpha: 0,
        stackType: "100%",
        ignoreAxisWidth: true
      }],
      graphs: graphs,
      categoryAxis: {
        parseDates: true,
        minPeriod: "MM",
        color: '#8ea9a9',
        gridAlpha: 0,
        labelsEnabled: true,
        axisAlpha: 0,
        gridPosition: "start",
        equalSpacing: true,
        guides: guides
      },
      categoryField: "month",
      chartCursor: {
        zoomable: false,
        bulletsEnabled: false,
        categoryBalloonEnabled: true,
        cursorColor: '#000000',
        leaveCursor: true,
        categoryBalloonFunction: (value) => {
          this.currentInterval = value;
          const rowIndex = this.monthlyRenewableMaxMin.findIndex(x => x.month.getTime() === value.getTime());
          const row = this.monthlyRenewableMaxMin[rowIndex];
          this.setCurrentFuelTypes(row);
          return formatDate(value, "MMM yyyy");
        }
      }
    };
  }
}


Vue.filter('formatSupplyNumber', (value: any) => {
  if (value < 10000) {
    return formatNumber(value, '0,00');
  } else {
    return formatNumber(value / 1000, '0,00.0');
  }
});



// WEBPACK FOOTER //
// src/views/electricity/nem/renewable-penetration/renewable-penetration.ts